import React from 'react';

import {AlertIcon} from './icons';
import {PlaceholderTemplate} from './placeholder-template';
import {reloadForChunkError} from '../../../helpers/app-update';
import {useTranslation} from '../../../hooks/use-translation';

import './scss/error-page.scss';

interface messageProps {
    heading: string;
    message?: string;
    /** e.g. the page's loading container, which places the message */
    className?: string;
    /** what comes under the message: the developer details, a `<footer>` with the buttons */
    children?: React.ReactNode;
}

/**
 * A page that could not be shown, calm and centred in the space it gets: what happened, what to do next, and the
 * buttons for that. The error page, the not-found page and a loader's retry share it.
 */
export const ErrorMessage = ({heading, message, className, children}: messageProps): React.JSX.Element => (
    <section className={'error-page' + (className ? ' ' + className : '')}>
        <AlertIcon large />
        <h1>{heading}</h1>
        {message && <p>{message}</p>}
        {children}
    </section>
);

interface props {
    error?: Error;
    info?: React.ErrorInfo;
}

const debugDomains = ['localhost', '.test', '127.0.0.1'];

const debugEnabled = (): boolean => debugDomains.some(domain => window.location.hostname.includes(domain));

/** The error and where it happened (the components, from the inside out), without the stack's indentation. */
const details = (error?: Error, info?: React.ErrorInfo): string =>
    [String(error ?? ''), ...(info?.componentStack ?? '').split('\n')]
        .map(line => line.trim())
        .filter(line => '' !== line)
        .join('\n');

/**
 * The error page of every error boundary (the routes', the layouts'). A chunk that failed to load, as a deploy removed
 * it, reloads the page instead (once, see `reloadForChunkError`). Developers see the error itself under the message;
 * the users do not, it is reported to Bugsnag for them.
 */
const ErrorPage = ({error, info}: props): React.JSX.Element => {
    const t = useTranslation();

    return reloadForChunkError(error) ? (
        <PlaceholderTemplate />
    ) : (
        <ErrorMessage heading={t('errorPage.heading')} message={t('errorPage.message')}>
            {debugEnabled() && <pre>{details(error, info)}</pre>}
            <footer>
                <button type="button" className="button" onClick={() => (window.location.href = '/')}>
                    {t('errorPage.home')}
                </button>
                <button type="button" className="button button-primary" onClick={() => window.location.reload()}>
                    {t('common.retry')}
                </button>
            </footer>
        </ErrorMessage>
    );
};

export default ErrorPage;
